import { useRef, useCallback, useEffect } from 'react'
import type { Window as WindowType } from '../../stores/windowStore'
import { useWindowStore } from '../../stores/windowStore'
import { Icon } from './Icon'
import './Window.css'

interface WindowProps {
  window: WindowType
  children: React.ReactNode
}

export function Window({ window, children }: WindowProps) {
  const {
    closeWindow,
    minimizeWindow,
    maximizeWindow,
    focusWindow,
    updateWindowPosition,
  } = useWindowStore()

  const windowRef = useRef<HTMLDivElement>(null)
  const isDragging = useRef(false)
  const dragOffset = useRef({ x: 0, y: 0 })

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if ((e.target as HTMLElement).closest('.window__controls')) return

      focusWindow(window.id)
      isDragging.current = true
      dragOffset.current = {
        x: e.clientX - window.position.x,
        y: e.clientY - window.position.y,
      }

      const handleMouseMove = (e: MouseEvent) => {
        if (!isDragging.current) return
        const x = Math.max(0, e.clientX - dragOffset.current.x)
        const y = Math.max(0, e.clientY - dragOffset.current.y)
        updateWindowPosition(window.id, x, y)
      }

      const handleMouseUp = () => {
        isDragging.current = false
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }

      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    },
    [window.id, window.position, focusWindow, updateWindowPosition]
  )

  const handleWindowClick = useCallback(() => {
    focusWindow(window.id)
  }, [window.id, focusWindow])

  if (window.state === 'minimized') {
    return null
  }

  const isMaximized = window.state === 'maximized'

  return (
    <div
      ref={windowRef}
      className={`window ${isMaximized ? 'window--maximized' : ''}`}
      style={{
        left: isMaximized ? 0 : window.position.x,
        top: isMaximized ? 0 : window.position.y,
        width: isMaximized ? '100%' : window.size.width,
        height: isMaximized ? `calc(100vh - var(--taskbar-height))` : window.size.height,
        zIndex: window.zIndex,
      }}
      onClick={handleWindowClick}
      role="dialog"
      aria-labelledby={`window-title-${window.id}`}
    >
      {/* Title Bar */}
      <div className="window__titlebar" onMouseDown={handleMouseDown}>
        <div className="window__title-content">
          <Icon name={window.icon} size={14} />
          <span id={`window-title-${window.id}`} className="window__title">
            {window.title}
          </span>
        </div>

        <div className="window__controls">
          <button
            className="window__control window__control--minimize"
            onClick={() => minimizeWindow(window.id)}
            aria-label="Minimize"
          >
            <Icon name="minimize" size={12} />
          </button>
          <button
            className="window__control window__control--maximize"
            onClick={() => maximizeWindow(window.id)}
            aria-label={isMaximized ? 'Restore' : 'Maximize'}
          >
            <Icon name={isMaximized ? 'restore' : 'maximize'} size={12} />
          </button>
          <button
            className="window__control window__control--close"
            onClick={() => closeWindow(window.id)}
            aria-label="Close"
          >
            <Icon name="close" size={12} />
          </button>
        </div>
      </div>

      {/* Window Content */}
      <div className="window__content">{children}</div>
    </div>
  )
}
